/*
 * related-products.js
 * Unminified version with tag-based exclusion and backfill.
 * - Products carrying any tag in EXCLUDED_TAGS are removed from both
 *   "Related products" and "Recently viewed".
 * - If filtering leaves fewer related products than the section's limit,
 *   extra recommendations are fetched (seeded from products already found)
 *   until the limit is reached or MAX_EXTRA_REQUESTS is used up.
 */
(function () {
  'use strict';

  // ---- Configuration -------------------------------------------------------
  const EXCLUDED_TAGS = ['ATTV_GLOBAL_EXCLUSION_TAG', 'Gift Options'];
  const MAX_RECOMMENDATIONS = 10; // Shopify's maximum per recommendations request
  const MAX_EXTRA_REQUESTS = 5;   // follow-up requests allowed when backfilling

  // ---- Selectors / attributes / classes (same as the original theme file) --
  const selectors = {
    apiRelatedTemplate: '[data-api-related-template]',
    productItem: '.product-item',
    gridItem: '[data-grid-item]',
    relatedSection: '[data-related-section]',
    recentlyViewed: 'recently-viewed',
    gridSlider: 'grid-slider',
    recentlyViewedWrapper: '[data-recently-viewed-wrapper]',
  };

  const attributes = {
    limit: 'data-limit',
    productId: 'data-product-id',
    layout: 'data-layout',
  };

  const classes = {
    hidden: 'hidden',
    gridMobileSlider: 'grid--mobile-slider',
    gridSlider: 'grid--slider',
  };

  // ---- Helpers -------------------------------------------------------------
  const excludedTags = EXCLUDED_TAGS.map((tag) => tag.trim().toLowerCase());
  const productCache = new Map(); // handle -> Promise<product JSON | null>

  function getProductHandle(item) {
    const link = item.matches('a[href*="/products/"]')
      ? item
      : item.querySelector('a[href*="/products/"]');
    if (!link) return null;

    const match = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function getProduct(handle) {
    if (!handle) return Promise.resolve(null);

    if (!productCache.has(handle)) {
      const root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      productCache.set(
        handle,
        fetch(`${root}products/${handle}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      );
    }

    return productCache.get(handle);
  }

  function hasExcludedTag(product) {
    if (!product) return false;
    return (product.tags || []).some((tag) =>
      excludedTags.includes(String(tag).trim().toLowerCase())
    );
  }

  // Remove cards whose product has an excluded tag (used for recently viewed).
  async function removeExcludedItems(items) {
    const list = Array.from(items);
    const products = await Promise.all(
      list.map((item) => getProduct(getProductHandle(item)))
    );

    list.forEach((item, index) => {
      if (hasExcludedTag(products[index])) {
        (item.closest(selectors.gridItem) || item).remove();
      }
    });
  }

  // Fetch the rendered recommendations section for one product.
  function fetchRecommendations(productId) {
    const url = `${window.theme.routes.product_recommendations_url}?section_id=api-product-recommendation&limit=${MAX_RECOMMENDATIONS}&product_id=${productId}&intent=related`;

    return fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const template = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(selectors.apiRelatedTemplate);
        const container = document.createElement('div');
        container.innerHTML = template ? template.innerHTML : '';
        return container;
      })
      .catch(() => null);
  }

  // ---- Custom element ------------------------------------------------------
  if (customElements.get('related-products')) return;

  customElements.define(
    'related-products',
    class RelatedProducts extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.id;
        this.relatedItems = 0;
        this.wrapper = this.querySelector(selectors.recentlyViewedWrapper);
        this.recentlyViewed = this.querySelector(selectors.recentlyViewed);
      }

      connectedCallback() {
        this.loadRelatedProducts();
        this.loadRecentlyViewedProducts();
      }

      async loadRelatedProducts() {
        const relatedSection = this.querySelector(selectors.relatedSection);
        if (!relatedSection) return;

        const currentId = String(relatedSection.getAttribute(attributes.productId));
        const limit = parseInt(relatedSection.getAttribute(attributes.limit), 10) || 4;

        const kept = [];                 // card elements to display
        const seenHandles = new Set();   // avoid duplicate cards
        const queuedIds = new Set([currentId]);
        const preferredSeeds = [currentId]; // products that passed the filter
        const fallbackSeeds = [];           // excluded products, used last
        let baseContainer = null;           // first response, reused for wrapper markup
        let requests = 0;

        while (
          kept.length < limit &&
          requests < 1 + MAX_EXTRA_REQUESTS &&
          (preferredSeeds.length || fallbackSeeds.length)
        ) {
          const seedId = preferredSeeds.length ? preferredSeeds.shift() : fallbackSeeds.shift();
          requests += 1;

          const container = await fetchRecommendations(seedId);
          if (!container) continue;

          const items = Array.from(container.querySelectorAll(selectors.gridItem));
          if (!items.length) continue;
          if (!baseContainer) baseContainer = container;

          const handles = items.map(getProductHandle);
          const products = await Promise.all(handles.map(getProduct));

          items.forEach((item, index) => {
            const handle = handles[index];
            const product = products[index];
            const excluded = hasExcludedTag(product);

            // Queue this product as a seed for further recommendations.
            if (product && !queuedIds.has(String(product.id))) {
              queuedIds.add(String(product.id));
              (excluded ? fallbackSeeds : preferredSeeds).push(String(product.id));
            }

            if (!handle || seenHandles.has(handle)) return;
            seenHandles.add(handle);

            if (product && String(product.id) === currentId) return;
            if (excluded) return;
            if (kept.length < limit) kept.push(item);
          });
        }

        this.renderRelatedProducts(relatedSection, baseContainer, kept);
      }

      renderRelatedProducts(relatedSection, baseContainer, kept) {
        if (!baseContainer || kept.length === 0) {
          this.hideSection(relatedSection);
          this.updateVisibility();
          return;
        }

        // Reuse the first response's wrapper markup, swapping in the kept cards.
        const grid = baseContainer.querySelector(selectors.gridItem).parentElement;
        baseContainer.querySelectorAll(selectors.gridItem).forEach((el) => el.remove());
        kept.forEach((card) => grid.appendChild(card));

        relatedSection.innerHTML = baseContainer.innerHTML;
        this.relatedItems = kept.length;

        const columnsMobile = parseInt(
          relatedSection.style.getPropertyValue('--COLUMNS-MOBILE'),
          10
        );
        const firstItem = relatedSection.querySelector(selectors.gridItem);

        if (columnsMobile === 0) {
          firstItem.parentElement.classList.add(classes.gridMobileSlider);
        }

        if (relatedSection.getAttribute(attributes.layout) === 'slider') {
          firstItem.parentElement.classList.add(classes.gridSlider);
        } else {
          firstItem.parentElement.classList.remove(classes.gridSlider);
        }

        this.updateVisibility();
      }

      loadRecentlyViewedProducts() {
        if (!this.recentlyViewed) return;

        this.recentlyViewed.addEventListener('theme:recently-viewed:loaded', () => {
          this.handleRecentlyViewedResponse();
        });
      }

      async handleRecentlyViewedResponse() {
        // Filter before anything is revealed or the slider is initialised.
        await removeExcludedItems(
          this.recentlyViewed.querySelectorAll(selectors.productItem)
        );

        const minimum = parseInt(this.recentlyViewed.dataset.minimum, 10) || 1;
        const items = this.recentlyViewed.querySelectorAll(selectors.productItem);
        const slider = this.recentlyViewed.querySelector(selectors.gridSlider);

        const showWithoutTabs = !this.wrapper && items.length > 0;
        const showWithTabs = this.wrapper && items.length >= minimum;

        if (showWithoutTabs || showWithTabs) {
          if (showWithTabs) this.wrapper.classList.remove(classes.hidden);
          this.recentlyViewed.classList.remove(classes.hidden);
          this.recentlyViewed.dispatchEvent(
            new CustomEvent('theme:tab:check', { bubbles: true })
          );
          if (slider) {
            slider.dispatchEvent(
              new CustomEvent('theme:grid-slider:init', { bubbles: true })
            );
          }
        }

        this.updateVisibility();
      }

      hideSection(element) {
        element.dispatchEvent(new CustomEvent('theme:tab:hide', { bubbles: true }));
      }

      updateVisibility() {
        const nothingToShow =
          Shopify.Products.getConfig().howManyToShow < 1 && this.relatedItems < 1;
        this.classList.toggle(classes.hidden, nothingToShow);
      }
    }
  );
})();